import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PageHeader, EmptyState, StatusBadge, Field, formatIDR } from "../components/Shared";
import { ProofImage } from "../components/ProofImage";
import { PAY } from "../constants/testIds";
import { Check, X, Plus, AlertTriangle, Landmark } from "lucide-react";
import { Link } from "react-router-dom";

const TABS = ["PENDING", "APPROVED", "REJECTED", "MISMATCH", "ALL"];

export default function PaymentQueue() {
  const { user } = useAuth();
  const { t } = useLang();
  const perms = user?.permissions || [];
  const [tab, setTab] = useState("PENDING");
  const [items, setItems] = useState([]);
  const [acting, setActing] = useState(null); // {id, kind}
  const [reason, setReason] = useState("");
  const [showManual, setShowManual] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/payments/queue?status=${tab}`);
      setItems(data.data.items);
    } catch (e) { toast.error(extractError(e).message); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/payments/${id}/approve`);
      toast.success(`${t("payments.approved")} · ${data.data.participant_code}`);
      load();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const decline = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/payments/${acting.id}/${acting.kind}`, { reason });
      toast.success(acting.kind === "reject" ? t("payments.rejected") : t("payments.mismatched"));
      setActing(null); setReason("");
      load();
    } catch (err) { toast.error(extractError(err).message); }
  };

  return (
    <div data-testid={PAY.queuePage} className="space-y-8">
      <PageHeader eyebrow={t("payments.eyebrow")} title={t("payments.queueTitle")} subtitle={t("payments.queueSubtitle")}>
        {perms.includes("payment_settings.view") && (
          <Button asChild data-testid="payment-settings-link" className="bg-white/10 hover:bg-white/15 text-white h-11">
            <Link to="/admin/payment-settings"><Landmark className="h-4 w-4 mr-1" /> {t("paymentSettings.title")}</Link>
          </Button>
        )}
        {perms.includes("enrollment.manage") && (
          <Button data-testid={PAY.manualBtn} onClick={() => setShowManual(!showManual)} className="bg-white/10 hover:bg-white/15 text-white h-11">
            <Plus className="h-4 w-4 mr-1" /> {t("payments.manualCreate")}
          </Button>
        )}
      </PageHeader>

      {showManual && <ManualEnroll onDone={() => { setShowManual(false); load(); }} />}

      <div className="flex gap-1 border-b border-white/5 overflow-x-auto">
        {TABS.map((s) => (
          <button key={s} data-testid={`payment-tab-${s}`} onClick={() => setTab(s)} className={`px-4 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === s ? "border-[#FF3B30] text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            {t(`payments.tab${s}`)}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState testId={PAY.queueEmpty} text={t("payments.queueEmpty")} />
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <Card key={p.payment_id} data-testid={`payment-row-${p.payment_id}`} className="bg-[#161923] border-white/5">
              <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                <ProofImage fileId={p.proof_file_id} url={p.proof_file_id ? null : p.proof_url} testId={`payment-proof-${p.payment_id}`} className="h-16 w-16" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{p.member_name}</span>
                    <span className="text-xs text-slate-500">{p.member_email}</span>
                    <StatusBadge status={p.status} testId={`payment-status-${p.payment_id}`} />
                  </div>
                  <div className="text-sm text-slate-400 mt-1">
                    {p.batch?.program_name} · {p.batch?.name} <span className="font-mono text-xs">({p.batch?.code})</span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-1.5 items-center">
                    <span data-testid={`payment-code-${p.payment_id}`} className="font-mono text-emerald-300 font-semibold">{p.participant_code}</span>
                    <span data-testid={`payment-total-${p.payment_id}`} className="text-[#F59E0B] font-semibold">{formatIDR(p.total_amount)} <span className="text-slate-600 font-normal">({formatIDR(p.base_amount)} + {p.unique_code})</span></span>
                    {p.payment_method && <span data-testid={`payment-method-${p.payment_id}`}>{t(`payments.method_${p.payment_method}`) !== `payments.method_${p.payment_method}` ? t(`payments.method_${p.payment_method}`) : p.payment_method}</span>}
                    {p.note && <span>“{p.note}”</span>}
                    {p.rejection_reason && (
                      <span data-testid={`payment-reason-${p.payment_id}`} className="text-red-300">
                        {t("payments.rejectReason")}: {p.rejection_reason.charAt(0).toUpperCase() + p.rejection_reason.slice(1)}
                      </span>
                    )}
                  </div>
                  {p.payment_bank_name && (
                    <div data-testid={`payment-destination-${p.payment_id}`} className="text-xs text-slate-400 mt-1.5">
                      {t("payments.destination")}: <span className="text-white">{p.payment_bank_name} {p.payment_account_number}</span>
                      {p.payment_account_holder_name && <span className="text-slate-500"> · a.n. {p.payment_account_holder_name}</span>}
                    </div>
                  )}
                </div>
                {p.status === "PENDING" && (
                  <div className="flex gap-2 flex-wrap">
                    {perms.includes("payment.approve") && (
                      <Button data-testid={`payment-approve-${p.payment_id}`} onClick={() => approve(p.payment_id)} className="bg-emerald-600 hover:bg-emerald-500 text-white h-9"><Check className="h-4 w-4 mr-1" /> {t("payments.approve")}</Button>
                    )}
                    {perms.includes("payment.reject") && (
                      <>
                        <Button data-testid={`payment-mismatch-${p.payment_id}`} onClick={() => setActing({ id: p.payment_id, kind: "mismatch" })} variant="ghost" className="text-amber-300 hover:bg-amber-500/10 h-9"><AlertTriangle className="h-4 w-4 mr-1" /> {t("payments.mismatch")}</Button>
                        <Button data-testid={`payment-reject-${p.payment_id}`} onClick={() => setActing({ id: p.payment_id, kind: "reject" })} variant="ghost" className="text-red-400 hover:bg-red-500/10 h-9"><X className="h-4 w-4 mr-1" /> {t("payments.reject")}</Button>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
              {acting?.id === p.payment_id && (
                <form onSubmit={decline} className="px-5 pb-5 flex flex-col sm:flex-row gap-2 sm:items-end">
                  <div className="flex-1"><Field label={acting.kind === "reject" ? t("payments.rejectReason") : t("payments.mismatchReason")} testId={acting.kind === "reject" ? PAY.rejectReason : PAY.mismatchReason} value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} /></div>
                  <Button data-testid={acting.kind === "reject" ? PAY.rejectConfirm : PAY.mismatchConfirm} type="submit" className={`${acting.kind === "reject" ? "bg-red-600 hover:bg-red-500" : "bg-amber-600 hover:bg-amber-500"} text-white h-10`}>{acting.kind === "reject" ? t("payments.confirmReject") : t("payments.confirmMismatch")}</Button>
                  <Button type="button" variant="ghost" onClick={() => setActing(null)} className="text-slate-300 h-10">{t("common.cancel")}</Button>
                </form>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ManualEnroll({ onDone }) {
  const { t } = useLang();
  const [batches, setBatches] = useState([]);
  const [form, setForm] = useState({ member_email: "", batch_id: "" });

  useEffect(() => { api.get("/batches").then(({ data }) => setBatches(data.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/enrollments", form);
      toast.success(`${t("enrollments.created")} · ${data.data.participant_code} · ${formatIDR(data.data.total_amount)}`);
      onDone();
    } catch (err) { toast.error(extractError(err).message); }
  };

  return (
    <Card data-testid={PAY.manualForm} className="bg-[#10121B] border-[#FF3B30]/30">
      <CardContent className="p-6">
        <p className="text-sm text-slate-400 mb-4">{t("enrollments.manualHint")}</p>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("payments.memberEmail")} testId={PAY.manualEmail} type="email" value={form.member_email} onChange={(e) => setForm({ ...form, member_email: e.target.value })} required />
          <div className="space-y-1.5">
            <label className="text-slate-300 text-sm">{t("programs.batches")}</label>
            <select data-testid={PAY.manualBatch} value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })} required className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
              <option value="">—</option>
              {batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{b.name} ({b.code})</option>)}
            </select>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button data-testid={PAY.manualSubmit} type="submit" className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">{t("enrollments.create")}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
